import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

export default class PageTurn {
  constructor(element) {
    this.element = element
    this.front = element.querySelector('.page-front')
    this.corner = element.querySelector('.peel-corner')

    this.state = { t: 0 }

    this.init()
  }

  init() {
    if (!this.front) return

    this.setup()
    this.createAnimation()
  }

  setup() {
    const full = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'

    gsap.set(this.front, {
      autoAlpha: 1,
      clipPath: full,
      webkitClipPath: full
    })

    if (this.corner) {
      // Parte a "altezza 0" (triangolo degenere): niente area visibile fino a quando inizia a crescere.
      const tinyCorner = 'polygon(100% 100%, 100% 100%, 100% 100%)'
      gsap.set(this.corner, {
        autoAlpha: 1,
        clipPath: tinyCorner,
        webkitClipPath: tinyCorner
      })
    }
  }

  createAnimation() {
    // Hold finale: completa l'animazione prima della fine dello scroll pinnato,
    // così rimane un tratto "fermo" prima che la pagina riprenda a scorrere.
    const holdPortion = 0.18
    const animPortion = 1 - holdPortion

    gsap.timeline({
      scrollTrigger: {
        trigger: '.section--hero',
        pin: true,
        start: 'top top',
        end: '+=2000',
        scrub: 0.3,
        anticipatePin: 1,
        markers: false
      }
    })
      .to(this.state, {
        t: 1,
        ease: 'none',
        duration: animPortion,
        onUpdate: () => this.render()
      })
      // Segmento di hold: nessun cambiamento, solo scroll "assorbito" mentre resta pinned
      .to({}, { duration: holdPortion })
  }

  render() {
    const t = this.state.t

    // Reveal diagonale: la parte rimossa nasce dal basso-destra e sale verso alto-sinistra.
    // La geometria ha 2 fasi:
    // 1) Peel diagonale fino a lasciare SOLO il triangolo alto-sinistra.
    // 2) Collasso di quel triangolo fino a 0 (così l'animazione finisce davvero).

    const cutEnd = 0.85

    if (t <= cutEnd) {
      const k = t / cutEnd
      const xBL = 100 * (1 - k)
      const yBR = 100 * (1 - k)

      // Front: include (0%, 100%) per non lasciare trasparenze sul lato sinistro.
      const frontPoly = `polygon(0% 0%, 100% 0%, 100% ${yBR}%, ${xBL}% 100%, 0% 100%)`
      gsap.set(this.front, { clipPath: frontPoly, webkitClipPath: frontPoly })

      // Corner: triangolino che cresce dall'angolo in basso a destra verso sinistra/alto
      if (this.corner) {
        // Base = segmento tagliato tra A(xBL,100) e B(100,yBR).
        // Punta = verso l'interno (alto-sinistra), così NON copre la parte esterna.
        const xMid = (xBL + 100) / 2
        const yMid = (100 + yBR) / 2
        // Altezza che parte da 0 e cresce gradualmente (ease-in), ma a k=1 arriva a (0,0)
        // così il triangolo diventa rettangolo (angolo retto in alto a sinistra).
        const kk = k * k
        const depth = kk * xMid
        const tipX = Math.max(0, xMid - depth)
        const tipY = Math.max(0, yMid - depth)
        const cornerPoly = `polygon(${xBL}% 100%, 100% ${yBR}%, ${tipX}% ${tipY}%)`
        gsap.set(this.corner, { clipPath: cornerPoly, webkitClipPath: cornerPoly })
      }
      return
    }

    // Fase 2: triangolo alto-sinistra che collassa (mantiene la diagonale)
    const v = (t - cutEnd) / (1 - cutEnd)
    const s = Math.max(0, 1 - v)

    const leg = 100 * s
    const frontPoly = `polygon(0% 0%, ${leg}% 0%, 0% ${leg}%)`
    gsap.set(this.front, { clipPath: frontPoly, webkitClipPath: frontPoly })

    // Corner: in fase 2 resta sul triangolo interno (punta verso l'alto-sinistra)
    if (this.corner) {
      const cornerPoly = `polygon(0% 0%, ${leg}% 0%, 0% ${leg}%)`
      gsap.set(this.corner, { clipPath: cornerPoly, webkitClipPath: cornerPoly })
    }
  }

  static init() {
    const element = document.querySelector('.hero-turnpage')
    if (element) new PageTurn(element)
  }
}
